import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Spinner, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { AgentAssessment, HumanQuestionRating, JudgingQuestion, ScoreBreakdown, ScrubbingReasoningSummary } from '../../../models';
import { buildPerQuestionComparison, parseScoreBreakdown } from './per-question-comparison';
import { classifyLoadError, ReasoningLoadError, reasoningLoadErrorMessage } from './reasoning-load-error';

interface ScoringReasoningDialogProps {
  isOpen: boolean;
  nominationSummary?: ScrubbingReasoningSummary;
  loadAssessments?: (nominationId: number) => Promise<AgentAssessment[]>;
  loadJudgingQuestions?: () => Promise<JudgingQuestion[]>;
  loadHumanRatings?: (nominationId: number) => Promise<HumanQuestionRating[]>;
  emptyStateLabel?: string;
  onClose(): void;
}

const useStyles = makeStyles({
  modalSurface: {
    maxWidth: '560px',
    width: '100%',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  overall: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  questions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
});

const ScoringReasoningDialogImpl: React.FC<ScoringReasoningDialogProps> = (props) => {
  const {
    isOpen,
    nominationSummary,
    loadAssessments,
    loadJudgingQuestions,
    loadHumanRatings,
    emptyStateLabel,
    onClose,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ReasoningLoadError | null>(null);
  const [breakdown, setBreakdown] = useState<ScoreBreakdown | null>(null);
  const [judgingQuestions, setJudgingQuestions] = useState<JudgingQuestion[]>([]);
  const [humanRatings, setHumanRatings] = useState<HumanQuestionRating[]>([]);

  useEffect(() => {
    if (!isOpen || !nominationSummary) {
      setError(null);
      setBreakdown(null);
      setJudgingQuestions([]);
      setHumanRatings([]);
      return;
    }

    let cancelled = false;
    const loadBreakdown = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);
      setBreakdown(null);
      setJudgingQuestions([]);
      setHumanRatings([]);
      try {
        const [assessments, questions, ratings] = await Promise.all([
          loadAssessments ? loadAssessments(nominationSummary.nominationId) : Promise.resolve([]),
          loadJudgingQuestions ? loadJudgingQuestions() : Promise.resolve([]),
          loadHumanRatings ? loadHumanRatings(nominationSummary.nominationId) : Promise.resolve([]),
        ]);
        const latestScoring = assessments
          .filter((assessment) => assessment.agentType === 2 && assessment.status === 'Completed')
          .sort((a, b) => b.createdDate.localeCompare(a.createdDate))[0];
        if (!cancelled) {
          setBreakdown(parseScoreBreakdown(latestScoring?.perQuestionScoresJson));
          setJudgingQuestions(questions);
          setHumanRatings(ratings);
        }
      } catch (err) {
        if (!cancelled) {
          setError(classifyLoadError(err));
          setBreakdown(null);
          setJudgingQuestions([]);
          setHumanRatings([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadBreakdown();
    return () => {
      cancelled = true;
    };
  }, [isOpen, nominationSummary?.nominationId]);

  const renderScoreBody = (): React.JSX.Element => {
    if (isLoading) {
      return <Spinner label='Loading score breakdown...' />;
    }

    if (error) {
      return <Text as='p' block>{reasoningLoadErrorMessage(error)}</Text>;
    }

    if (!breakdown || breakdown.questions.length === 0) {
      const showNoJudgingLabel = judgingQuestions.length === 0 && !!emptyStateLabel;
      return <Text as='p' block>{showNoJudgingLabel ? emptyStateLabel : 'No AI score breakdown is available for this nomination.'}</Text>;
    }

    const rows = buildPerQuestionComparison(breakdown.questions, humanRatings, judgingQuestions);

    return (
      <>
        {breakdown.overall && (
          <div className={styles.overall}>
            <Text weight='semibold' block>Overall score: {breakdown.overall.score ?? '-'}</Text>
            {breakdown.overall.rationale && <Text as='p' block>{breakdown.overall.rationale}</Text>}
          </div>
        )}
        <div className={styles.questions}>
          {rows.map((row) => (
            <div key={row.questionId} className={styles.question}>
              <Text weight='semibold' block>{row.questionText}</Text>
              <Text className='text--blue' block>Score: {row.aiScore} / 5</Text>
              {row.raterCount > 0
                ? <Text block>Judge avg: {row.humanAverage?.toFixed(1) ?? '-'} / 5 (n={row.raterCount})</Text>
                : <Text block>No judge ratings</Text>}
              {row.aiRationale && <Text as='p' block>{row.aiRationale}</Text>}
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, styles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={() => onClose()}
              />
            )}
          >
            AI vs judge score breakdown
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            {nominationSummary && (
              <Text className='text--blue' weight='semibold' block>
                {nominationSummary.title}
              </Text>
            )}
            {renderScoreBody()}
          </DialogContent>
          <DialogActions>
            <Button appearance='secondary' onClick={() => onClose()}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const ScoringReasoningDialog = observer(ScoringReasoningDialogImpl);
